// Helpers
import { to } from '../../../lib/helpers';

// Requests
import {
  userGetSubscriptionsRequest,
  userUpdateSubscriptionsRequest,
} from '../../../backend/userRequests';

import { pushMessageBanner } from '../../../reducers/messageBanner/messageBannerActions';
import { selectAllLinksAsArray } from '../../agency/agencySelectors';

// Constants
export const STORE_SUBSCRIPTIONS_DATA = '[USER] Store Notifications Data';

/** convert the backend response to ui friendly object

        the backend response in the form
        [
          { type: 'DMV_REMINDER', subscriptions: ['PM', 'PE'] },
          { type: 'TXT_UPDATE', subscriptions: ['PM'] },
        ]

        it gets converted to the form
      { showReminders : true,
          subscriptions: {
          email_reminder : true,
          text_reminder: true,
          email_newFeatures: false,
          text_newFeatures: true
       }
      }
      hasDmvLink - temporarily added since backend is not checking this.
       Needs to be removed after backend implements it in M&O
  **/

const convertToUISubscriptionsObject = (response, hasDmvLink) => {
  let showReminders = hasDmvLink;

  let defaultValues = {
    ...(showReminders && { email_reminders: false, text_reminders: false }),
    email_newFeatures: false,
    text_newFeatures: false,
  };

  const subscriptions = response.reduce((acc, curr) => {
    switch (curr.type) {
      case 'DMV_REMINDER':
        showReminders = true;
        acc['email_reminder'] = curr.subscriptions.includes('PE');
        acc['text_reminder'] = curr.subscriptions.includes('PM');
        break;
      case 'TXT_UPDATE':
        acc['email_newFeatures'] = curr.subscriptions.includes('PE');
        acc['text_newFeatures'] = curr.subscriptions.includes('PM');
        break;
      default:
        break;
    }
    return acc;
  }, {});

  return {
    subscriptions: { ...defaultValues, ...subscriptions },
    showReminders,
  };
};

/** convert form values into the payload that backend is expecting
     
        form values are in the form
        {
          email_reminder : true,
          text_reminder: true,
          email_newFeatures: false,
          text_newFeatures: true
        }

        converts to the backend payload 
        [
          { type: 'DMV_REMINDER', subscriptions: ['PM', 'PE'] },
          { type: 'TXT_UPDATE', subscriptions: ['PM'] },
        ]
     
  **/
const convertToPayload = values => {
  let payload = [];

  if (
    values.hasOwnProperty('email_reminder') ||
    values.hasOwnProperty('text_reminder')
  ) {
    let subscription = {
      type: 'DMV_REMINDER',
      subscriptions: [],
    };
    if (values.email_reminder) {
      subscription.subscriptions.push('PE');
    }
    if (values.text_reminder) {
      subscription.subscriptions.push('PM');
    }
    payload.push(subscription);
  }

  if (
    values.hasOwnProperty('email_newFeatures') ||
    values.hasOwnProperty('text_newFeatures')
  ) {
    let subscription = {
      type: 'TXT_UPDATE',
      subscriptions: [],
    };
    if (values.email_newFeatures) {
      subscription.subscriptions.push('PE');
    }
    if (values.text_newFeatures) {
      subscription.subscriptions.push('PM');
    }
    payload.push(subscription);
  }

  return payload;
};

export const getSubscriptions = () => {
  return async (dispatch, getState) => {
    try {
      const [response] = await to(dispatch(userGetSubscriptionsRequest()));
      if (response.response.status !== 200) throw new Error(response);
      const links = selectAllLinksAsArray(getState());
      const hasDmvLink = links.some(
        link => link.agencyId === '9' && link.appId === '8'
      );

      const notifications = convertToUISubscriptionsObject(
        response.body.data,
        hasDmvLink
      );

      dispatch({
        type: STORE_SUBSCRIPTIONS_DATA,
        payload: {
          getNotificationsStatus: response.body.status,
          ...notifications,
        },
      });

      return Promise.resolve(response.body.data);
    } catch (error) {
      dispatch({
        type: STORE_SUBSCRIPTIONS_DATA,
        payload: {
          getNotificationsStatus: 'EXCEPTION',
        },
      });

      return Promise.resolve({});
    }
  };
};

export const updateSubscriptions = ({
  values: { ...values },
  props: { setToggle },
}) => async (dispatch, getState) => {
  const payload = convertToPayload({ ...values });

  try {
    const [response] = await to(
      dispatch(userUpdateSubscriptionsRequest(payload))
    );
    if (response.response.status !== 200) throw new Error('error');
    const links = selectAllLinksAsArray(getState());
    const hasDmvLink = links.some(
      link => link.agencyId === '9' && link.appId === '8'
    );

    const notifications = convertToUISubscriptionsObject(
      response.body.data,
      hasDmvLink
    );

    setToggle();
    dispatch({
      type: STORE_SUBSCRIPTIONS_DATA,
      payload: {
        updateNotificationsStatus: response.body.status,
        ...notifications,
      },
    });
    return Promise.resolve(response.body.data);
  } catch (error) {
    setToggle();
    dispatch({
      type: STORE_SUBSCRIPTIONS_DATA,
      payload: {
        updateNotificationsStatus: 'EXCEPTION',
      },
    });
    const messageIdTime = Date.now();
    dispatch(
      pushMessageBanner([
        {
          messageId: messageIdTime.toString(),
          textEnglish: `{"type": "Modals", "id": "CouldnotUpdateNotificationSettings"}`,
          textSpanish: `{"type": "Modals", "id": "CouldnotUpdateNotificationSettings"}`,
          startTime: '2021-10-01 18:35:00.0',
          endTime: '2040-06-30 12:05:00.0',
          priority: '1',
          status: 'R',
          author: 'USER',
          valid: true,
        },
      ])
    );
    return Promise.resolve({});
  }
};
